import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import debounce from 'lodash/debounce';

import { DeviceContext, deviceType } from '@providers';

import DesktopSearchPage from './DesktopSearchPage';
import MobileSearchPage from './MobileSearchPage';

// queries
import { UI_COMPONENT } from '@src/queries';
import { uiComponent } from '@src/queries/__generated__/uiComponent';
import { uiComponentVariables } from '@src/core/config';

// utils
import { getSlugFromText, getDBIdFromGraphqlId, gaV4SearchStart, gaV4SearchResultAction } from '@utils';
import { findBrandByName } from '@src/utils';
import { goToUrl } from '@src/utils/urlCheck';
import { elasticSearchProductResult, elasticSearchBrandResult, elasticSearchTagResult } from '@src/utils/elasticSearch';
import { searchResultUrl, productsUrl } from '@src/app/routes';
import { getInvisibleBrandIds } from '@src/utils/constants';

interface ILocationState {
  prevSearchQuery: string;
}

interface RecentlyViewProduct {
  id: number;
  thumbnailUrl: string;
  urlForGraphql: string;
}

const SearchPage = () => {
  const { isMobile } = useContext<deviceType>(DeviceContext);

  const history = useHistory();
  const location = useLocation<ILocationState>();
  const [searchValue, setSearchValue] = useState('');

  // searchValue 없을 때 data
  const [recentlyViewedProducts] = useState<RecentlyViewProduct[]>(
    JSON.parse(localStorage.getItem('recent-products') || '[]'),
  );
  const [recentlySearchedKeywords] = useState(JSON.parse(localStorage.getItem('keywords') || '[]'));
  const [mostTrendingKeywords, setMostTrendingKeywords] = useState([]);

  // searchValue 있을 때 data
  const category = JSON.parse(localStorage.getItem('category') || '[]');
  const categoryFilter = (el) => el.node.name.toLowerCase().includes(searchValue.toLowerCase());

  const [searchedBrands, setSearchedBrands] = useState([]);
  const [searchedProducts, setSearchedProducts] = useState([]);
  const [searchedTags, setSearchedTags] = useState([]);

  const { data } = useQuery<uiComponent>(UI_COMPONENT, {
    variables: uiComponentVariables.trendingKeywordsMobile,
    onCompleted: (data) => {
      const trendingKeywordsData = data?.uicomponent?.items;
      const activeTrendingKeywordsData = trendingKeywordsData.filter((trendingKeyword) => trendingKeyword.isActive);

      const maxItems = isMobile ? 20 : 10;
      if (activeTrendingKeywordsData?.length > maxItems) {
        const sliceTrendingData = activeTrendingKeywordsData.slice(0, maxItems);
        setMostTrendingKeywords(sliceTrendingData);
      } else {
        setMostTrendingKeywords(activeTrendingKeywordsData);
      }
    },
  });

  const debouncedSetSearchResultStateFunc = useMemo(
    () =>
      debounce(async (searchValue: string) => {
        const products = await elasticSearchProductResult(searchValue);
        const brands = await elasticSearchBrandResult(searchValue);
        const tags = await elasticSearchTagResult(searchValue);

        setSearchedProducts(products);
        setSearchedBrands(brands);
        setSearchedTags(tags);
      }, 300),
    [],
  );

  useEffect(() => {
    if (location?.state?.prevSearchQuery) {
      setSearchValue(location?.state?.prevSearchQuery);

      // TODO: min_gram 설정
      if (location?.state?.prevSearchQuery?.length >= 2) {
        debouncedSetSearchResultStateFunc(location?.state?.prevSearchQuery);
      }
    }
  }, [location?.state?.prevSearchQuery, debouncedSetSearchResultStateFunc]);

  useEffect(() => {
    return () => {
      debouncedSetSearchResultStateFunc.cancel();
    };
  }, [debouncedSetSearchResultStateFunc]);

  useEffect(() => {
    localStorage.setItem('recent-products', JSON.stringify(recentlyViewedProducts));
  }, [recentlyViewedProducts]);

  const goBack = () => {
    history.goBack();
  };

  const handleClearValue = () => {
    setSearchValue('');
  };

  const handleSearchInputFocus = async () => {
    await gaV4SearchStart();
  };

  const handleSearchValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchValue = event.target.value;
      setSearchValue(newSearchValue);

      if (newSearchValue.length >= 2) {
        debouncedSetSearchResultStateFunc(newSearchValue);
      }
    },
    [debouncedSetSearchResultStateFunc],
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (searchValue.trim() !== '') {
      if (recentlySearchedKeywords.length <= 9) {
        addRecentlyKeyword(searchValue);
      } else {
        deleteRecentlyKeyword(searchValue);
      }

      // TODO : Change elastic search brand list
      const brandList = JSON.parse(localStorage.getItem('brand-list'));
      const coincideBrand = findBrandByName(brandList, searchValue);

      // 숨김 브랜드는 이동 제외
      if (
          coincideBrand &&
          !getInvisibleBrandIds().includes(String(coincideBrand._id))
      ) {
        goToUrl(history, coincideBrand.url);
      } else {
        history.push(`${searchResultUrl}?q=${searchValue}`);
      }
    }
  };

  const handleOnSearchEvent = async (actionType, keyword, keywordId, searchText) => {
    await gaV4SearchResultAction(actionType, keywordId, keyword, searchText);
  };

  const handleKeywordClick = (keyword) => {
    history.push(`${searchResultUrl}?q=${keyword}`);
  };

  const handleClickBrandListItem = (brand) => {
    history.push(`${brand['_source']['url']}`);
    handleOnSearchEvent('brand', brand['_source']['name'], brand['_id'], searchValue);
  };

  const handleClickCategoryListItem = (category) => {
    history.push(
      `${productsUrl}category/${getSlugFromText(category.name)}-${getDBIdFromGraphqlId(category.id, 'Category')}`,
    );
    handleOnSearchEvent('category', category.name, getDBIdFromGraphqlId(category.id, 'Category'), searchValue);
  };

  const handleClickProductListItem = (product) => {
    history.push(`${product['_source']['url']}`);
    handleOnSearchEvent('submit', product['_source']['name'], product['_id'], searchValue);
  };

  const handleClickTagListItem = (tagItem) => {
    const tag = tagItem['first_tag']['hits']['hits'][0]['_source']['tag'];

    history.push(
      `${productsUrl}tag/${tag.toLowerCase().replace(/[\/ ]/g, '-')}-${getDBIdFromGraphqlId(
        tagItem['key'],
      ).toString()}?name=${tag}`,
    );
    handleOnSearchEvent('tag', tag, getDBIdFromGraphqlId(tagItem['key']).toString(), searchValue);
  };

  // 최근 검색어 관리
  const addRecentlyKeyword = (text: string) => {
    const newKeyword = { text };
    const newRecentlyKeywordList = [newKeyword, ...recentlySearchedKeywords];

    localStorage.setItem('keywords', JSON.stringify(newRecentlyKeywordList));
  };

  const deleteRecentlyKeyword = (text: string) => {
    const newKeyword = { text };
    const newRecentlyKeywordList = [newKeyword, ...recentlySearchedKeywords].slice(0, 10);

    localStorage.setItem('keywords', JSON.stringify(newRecentlyKeywordList));
  };

  const searchProps = {
    // state
    searchValue,
    recentlyViewedProducts,
    recentlySearchedKeywords,
    mostTrendingKeywords,
    category,
    searchedBrands,
    searchedProducts,
    searchedTags,

    // filter func
    categoryFilter,

    // event handler
    goBack,
    handleClearValue,
    handleSearchInputFocus,
    handleSearchValueChange,
    handleSubmit,
    handleKeywordClick,
    handleClickBrandListItem,
    handleClickCategoryListItem,
    handleClickProductListItem,
    handleClickTagListItem,
  };

  return isMobile ? <MobileSearchPage {...searchProps} /> : <DesktopSearchPage {...searchProps} />;
};

export default SearchPage;
